import { BottomActionBar } from '@components/Common/BottomActionBar.js';
import toast from '@components/Common/Toast.js';
import { DrawerAiAnalyser } from '@components/Drawer/AiAnalyzer.js';
import YesNoPromptModal from '@components/Modals/YesNoPromptModal.js';
import { IS_MOBILE } from '@constants/global.js';
import { BULK_ACTION_LIMIT } from '@constants/project.js';
import {
  AiIcon,
  ChevronIcon,
  CopyIcon,
  EditIcon,
  MoveFileIcon,
  ShareIcon,
  TrashIcon,
} from '@icons/IconLibrary.js';
import MainDrawer from '@layouts/Drawer.js';
import { route } from '@router/index.js';
import {
  COLUMN_EVENT_TYPES,
  columnUpdate,
  projectListReOrder,
} from '@signals/project/index.js';
import DrawerState from '@states/DrawerState.js';
import FoldersState from '@states/FoldersState.js';
import ProjectsState, {
  AiSelectedItems,
  CurrentItemsState,
  LoadedFolderIds,
} from '@states/Projects.js';
import { UIButton, UIDiv, UIText } from '@ui';
import {
  getCurrentSortInfo,
  getVisibleColumns,
  sortItems,
  sortItemsByRowOrder,
} from '@utils/columnUtils.js';
import {
  deleteProjects,
  deleteTasks,
  duplicateProject,
  duplicateTask,
  getSharedProjectId,
  moveTask,
  shareProjectTask,
  updateRowOrders,
} from '@utils/projectUtils.js';
import { SelectionManager } from '@utils/SelectionManager.js';
import Fuse from 'fuse.js';
import MoveModal from '../Modals/MoveModal.js';
import CreateTaskRow from './CreateTaskRow.js';
import ProjectListColumns from './ProjectListColumns.js';
import ProjectListViewRow from './ProjectListViewRow.js';

export default class ProjectListView extends UIDiv {
  constructor(
    accessControl,
    currentFolderId = null,
    selectedProjectType,
    isAnonymousView = false,
    searchTerm = '',
  ) {
    super();

    this.canCreate = accessControl.hasPermission('create');
    this.canUpdate = accessControl.hasPermission('update');
    this.canDelete = accessControl.hasPermission('delete');
    this.canShare = accessControl.hasPermission('share');
    this.canMove = accessControl.hasPermission('move');
    this.canDownload = accessControl.hasPermission('download');
    this.currentFolderId = currentFolderId;
    this.selectedProjectType = selectedProjectType; // Track selected project type
    this.rowsContainer = null; // Store reference to rows container
    this.rowsWrapper = null; // Store reference to rows wrapper
    this.realTimeUpdateThrottle = null; // Throttle real-time updates
    this.searchTerm = searchTerm; // Store search term for filtering
    CurrentItemsState.setCurrentItems([]); // Track current items to detect changes
    this.selectedItems = []; // Track selected item IDs for actions
    this.isAnonymousView = isAnonymousView; // Track if the view is anonymous

    // // Virtual scrolling properties
    // this.virtualScrolling = {
    //   enabled: true,
    //   itemHeight: 48, // Approximate height of each row in pixels
    //   containerHeight: 0,
    //   visibleStart: 0,
    //   visibleEnd: 0,
    //   scrollTop: 0,
    //   renderBuffer: 5, // Extra items to render above/below visible area
    //   maxSyncItems: 50, // Maximum items to render synchronously
    // };

    this.rowElements = new Map(); // Cache for row elements
    // this.visibleRows = new Set(); // Track currently visible rows

    this.sharedProjectId = getSharedProjectId();

    this.addClass(
      'project-list-view flex flex-col w-full h-full border border-gray-200 border-collapse select-none rounded-lg shadow-sm overflow-auto',
    );

    // Subscribe to column events to sync rows with column changes
    columnUpdate.add(data => {
      this.#handleColumnChange(data);
    });

    // Subscribe to ProjectsState changes to refresh rows when projects are added/removed/updated
    this.subscribeFromProjectsState = ProjectsState.subscribe(projects => {
      this.#handleProjectsStateChange(projects);
    });

    // Add navigation event listener
    this.dom.addEventListener(
      'navigateToFolder',
      this.navigateToFolder.bind(this),
    );

    // Add row reorder event listener for drag and drop
    projectListReOrder.add(data => {
      this.#handleRowReorder(data);
    });

    this.add(this.#createColumnsLayout());
    this.add(this.#createRows());
    if (!this.isAnonymousView) {
      this.#initSelectionManager();
      this.#initBottomActionBar();
    }
  }

  #createColumnsLayout() {
    const columnsContainer = new UIDiv();
    columnsContainer.addClass(
      'columns-container w-full flex flex-col relative min-w-fit sticky top-0 bg-white relative min-w-fit z-30',
    );

    // Add breadcrumb navigation if we're inside a folder and not in shared project view (project ID is null)
    if (this.currentFolderId) {
      const breadcrumbContainer = this.#createBreadcrumbNavigation();
      columnsContainer.add(breadcrumbContainer);
    }

    // Create the main columns container
    const mainColumnsContainer = new UIDiv();
    mainColumnsContainer.addClass('w-full flex relative min-w-fit');

    // Create scrollable columns and store reference for cleanup
    this.listViewColumns = new ProjectListColumns(
      this.currentFolderId,
      this.canCreate,
      this.canUpdate,
      this.isAnonymousView,
    );

    // Create fixed actions column
    const actionsColumn = this.#createActionsColumn();

    mainColumnsContainer.add(this.listViewColumns, actionsColumn);
    columnsContainer.add(mainColumnsContainer);
    if (this.canCreate) this.#createTaskAddRow(columnsContainer);

    return columnsContainer;
  }

  #createBreadcrumbNavigation() {
    const breadcrumbContainer = new UIDiv();
    breadcrumbContainer.addClass(
      'w-full p-3 bg-gray-50 border-b border-gray-200 flex items-center gap-1',
    );

    // Back button
    const backButton = new UIButton(i18next.t('pages.projects.projects'));
    backButton.addClass('!filter-none text-sm hover:text-blue-800 font-medium');

    backButton.onClick(() => {
      this.navigateToAllProjects();
    });

    const chevronIcon = new ChevronIcon({
      size: 12,
      color: 'none',
    });

    // Current folder name
    const currentFolder = ProjectsState.projects.folders.find(
      f => f.id === this.currentFolderId,
    );
    const folderName = currentFolder
      ? currentFolder.folderName || currentFolder.name
      : i18next.t('pages.projects.unknownFolder');

    const folderLabel = new UIText(folderName);
    folderLabel.addClass('text-sm font-semibold');

    if (!this.sharedProjectId) {
      breadcrumbContainer.add(backButton);
      breadcrumbContainer.add(chevronIcon);
    } else {
      // If in shared project view, don't show back button and just show a label with 'Shared Project'
      const sharedLabel = new UIText(i18next.t('common.shared_projects'));
      sharedLabel.addClass('text-sm font-semibold text-gray-600');
      breadcrumbContainer.add(sharedLabel);
      breadcrumbContainer.add(chevronIcon);
    }
    breadcrumbContainer.add(folderLabel);

    return breadcrumbContainer;
  }

  #createActionsColumn() {
    const actionsWrapper = new UIDiv();
    actionsWrapper.setId('column-actions');
    actionsWrapper.addClass(
      'p-2 flex-shrink-0 bg-white border-b border-l border-gray-200 sticky right-0 z-20',
    );
    actionsWrapper.setStyle('width', ['70px']);
    actionsWrapper.setStyle('min-width', ['70px']);
    actionsWrapper.setStyle('max-width', ['70px']);

    // Actions column header
    const header = new UIDiv();
    header.addClass('text-center');

    const title = new UIText(i18next.t('pages.projects.actions'));
    title.addClass('font-medium text-gray-900 truncate block');
    header.add(title);

    actionsWrapper.add(header);

    return actionsWrapper;
  }

  #createRows() {
    this.rowsContainer = new UIDiv();
    this.rowsContainer.addClass('flex-1 min-w-fit');

    // Add scroll event listener for virtual scrolling
    // this.rowsContainer.dom.addEventListener(
    //   'scroll',
    //   this.handleScroll.bind(this),
    // );

    // Get projects data from ProjectsState
    const { folders = [] } = ProjectsState.projects;

    // Create rows container with scroll
    this.rowsWrapper = new UIDiv();
    this.rowsWrapper.addClass('w-full min-w-fit relative');

    // Set up drag and drop for the rows container
    this.#setupRowDragAndDrop();

    if (!folders || !Array.isArray(folders)) {
      // No data message
      const noDataMessage = new UIDiv();
      noDataMessage.addClass(
        'flex items-center justify-center h-32 text-gray-500',
      );
      noDataMessage.add(new UIText(i18next.t('pages.projects.noItemsFound')));
      this.rowsWrapper.add(noDataMessage);
    } else {
      this.#createRowItems();
    }

    this.rowsContainer.add(this.rowsWrapper);
    return this.rowsContainer;
  }

  #createTaskAddRow(container) {
    // Create and add the create task row if applicable
    if (this.currentFolderId !== null && this.canCreate) {
      const createTaskRow = new CreateTaskRow(this.currentFolderId);
      container.add(createTaskRow);
    }
  }

  #createRowItems() {
    const { folders = [], tasks = [] } = ProjectsState.projects;

    // if this.currentFolderId is null, it means we are displaying all projects
    // otherwise, we filter by currentFolderId to find the relevant tasks related to the folderId
    let items = [];

    if (this.currentFolderId === null) {
      items = [...folders];
    }
    // If currentFolderId is set, filter tasks related to that folder
    else {
      // Filter tasks by currentFolderId
      items = tasks.filter(task => task.parentId === this.currentFolderId);
    }

    // Apply search filtering if search term exists
    if (this.searchTerm && this.searchTerm.trim()) {
      items = this.#getFilteredItems(items);
    }

    // Apply sorting if there's a sorted column
    const sortInfo = getCurrentSortInfo(this.currentFolderId);
    if (sortInfo) {
      items = sortItems(
        items,
        sortInfo.columnKey,
        sortInfo.direction,
        this.currentFolderId,
      );
    } else {
      // Sort by rowOrder if no other sorting is applied
      items = sortItemsByRowOrder(items);
    }

    // Store current items for incremental updates
    CurrentItemsState.setCurrentItems([...items]);

    // Handle different rendering strategies based on item count
    if (items.length === 0) {
      // Show "no results" message when search returns empty or no data
      const noResultsDiv = new UIText(i18next.t('pages.projects.noItemsFound'));
      noResultsDiv.addClass(
        'flex items-center justify-center h-32 text-gray-500',
      );
      this.rowsWrapper.add(noResultsDiv);
      // } else if (items.length <= this.virtualScrolling.maxSyncItems) {
    } else this.#renderItemsSynchronously(items);
    // } else {
    //   this.setupVirtualScrolling(items);
  }

  #createRowForItem(item, isTask, index) {
    // remove customColumns from item to avoid memory leaks
    const { customColumns, ...itemWithoutCustomColumns } = item;
    const row = new ProjectListViewRow(
      itemWithoutCustomColumns,
      isTask,
      this.canCreate,
      this.canUpdate,
      this.canShare,
      this.canDelete,
      this.canMove,
      this.canDownload,
      this.currentFolderId, // Pass currentFolderId to the row
      this.selectedProjectType,
      this.isAnonymousView,
    );

    row.addClass('bg-white hover:bg-blue-50 transition-colors duration-200');

    return row;
  }

  /**
   * Filter items using Fuse.js search
   * @param {Array} items - Items to filter
   * @returns {Array} - Filtered items
   */
  #getFilteredItems(items) {
    if (!this.searchTerm || !this.searchTerm.trim()) {
      return items;
    }

    const fuseOptions = {
      keys: ['name', 'columnValues.value', 'deadline', 'status', 'assignees'],
      threshold: 0.3,
      includeScore: true,
      shouldSort: true,
    };

    const fuse = new Fuse(items, fuseOptions);
    const results = fuse.search(this.searchTerm);

    // Filter results with score <= 0.4 and return the items
    return results
      .filter(result => result.score <= 0.4)
      .map(result => result.item);
  }

  /**
   * Handle search input and refresh the view
   * @param {string} searchTerm - The search term
   */
  handleSearch(searchTerm) {
    this.searchTerm = searchTerm ? searchTerm.toLowerCase() : '';
    this.#refreshRows();
  }

  /**
   * Handle ProjectsState changes to automatically refresh the rows and columns
   * @param {Object} projectsState - The updated projects state
   */
  #handleProjectsStateChange(projectsState) {
    // track the selected project type looking at the ProjectsState.rootFolderOrganizationId
    const selectedProjectType =
      projectsState.rootFolderOrganizationId !== null ? 'team' : 'personal';
    // If the selected project type has changed, update the selected project type
    // Note: We don't reset currentFolderId here because this can be triggered by background loading
    // The currentFolderId should only be changed by explicit navigation events
    if (
      this.selectedProjectType !== 'shared' &&
      this.selectedProjectType !== selectedProjectType
    ) {
      this.selectedProjectType = selectedProjectType;
    }
    // Only update if not loading and we have data
    if (
      projectsState.isLoading ||
      !projectsState.folders ||
      projectsState.subscriptionError
    ) {
      return;
    }

    if (
      this.searchTerm ||
      !CurrentItemsState.currentItems ||
      CurrentItemsState.currentItems.length === 0
    ) {
      // We need remove the "no items found"
      if (this.rowsWrapper) this.rowsWrapper.clear();
    }
    // Get the new items (same logic as #createRowItems)
    const { folders = [], tasks = [] } = projectsState;
    let newItems = [];

    if (this.currentFolderId === null) {
      newItems = [...folders];
    } else {
      newItems = tasks.filter(task => task.parentId === this.currentFolderId);
    }

    // Apply sorting if there's a sorted column
    const sortInfo = getCurrentSortInfo(this.currentFolderId);
    if (sortInfo) {
      newItems = sortItems(
        newItems,
        sortInfo.columnKey,
        sortInfo.direction,
        this.currentFolderId,
      );
    } else {
      // Sort by rowOrder if no other sorting is applied
      newItems = sortItemsByRowOrder(newItems);
    }

    // Detect what changed
    const addedItems = this.#getAddedItems(
      CurrentItemsState.currentItems,
      newItems,
    );
    const removedItems = this.#getRemovedItems(
      CurrentItemsState.currentItems,
      newItems,
    );
    // const hasColumnChanges = this.#hasColumnValuesChanged(
    //   CurrentItemsState.currentItems,
    //   newItems,
    // );

    // If items were removed, column values changed, or the count doesn't match our expectations, do a full refresh
    if (
      removedItems.length > 0 ||
      // Previously, this forced a full refresh of all rows and columns after editing a cell. - I don't remember for which case I did this
      // However, it introduced bugs, so it's now commented out to prevent those issues.
      // hasColumnChanges ||
      (addedItems.length === 0 &&
        newItems.length !== CurrentItemsState.currentItems.length)
    ) {
      this.#refreshRowsAndColumns();
      return;
    }

    // If only new items were added, append them incrementally
    if (addedItems.length > 0) {
      this.#appendNewItems(addedItems);
      CurrentItemsState.setCurrentItems([...newItems]); // Update our tracking
    }
  }

  /**
   * Get items that were added compared to the previous list
   * @param {Array} oldItems - Previous items list
   * @param {Array} newItems - New items list
   * @returns {Array} - Items that were added
   */
  #getAddedItems(oldItems, newItems) {
    const oldIds = new Set(oldItems.map(item => item.id));
    return newItems.filter(item => !oldIds.has(item.id));
  }

  /**
   * Get items that were removed compared to the previous list
   * @param {Array} oldItems - Previous items list
   * @param {Array} newItems - New items list
   * @returns {Array} - Items that were removed
   */
  #getRemovedItems(oldItems, newItems) {
    const newIds = new Set(newItems.map(item => item.id));
    return oldItems.filter(item => !newIds.has(item.id));
  }

  /**
   * Check if any items have changed column values compared to the previous list
   * @param {Array} oldItems - Previous items list
   * @param {Array} newItems - New items list
   * @returns {boolean} - True if any column values have changed
   */
  #hasColumnValuesChanged(oldItems, newItems) {
    if (oldItems.length !== newItems.length) {
      return true;
    }

    // Create maps for O(1) lookup
    const oldItemsMap = new Map(oldItems.map(item => [item.id, item]));

    for (const newItem of newItems) {
      const oldItem = oldItemsMap.get(newItem.id);
      if (!oldItem) {
        continue; // New item, will be handled by getAddedItems
      }

      // Compare columnValues if they exist
      if (newItem.columnValues || oldItem.columnValues) {
        const newColumnValues = newItem.columnValues || [];
        const oldColumnValues = oldItem.columnValues || [];

        // Quick length check
        if (newColumnValues.length !== oldColumnValues.length) {
          return true;
        }

        // Create maps for column values comparison
        const oldValuesMap = new Map(
          oldColumnValues.map(cv => [cv.columnId, cv.value]),
        );

        // Check if any column value has changed
        for (const newColumnValue of newColumnValues) {
          const oldValue = oldValuesMap.get(newColumnValue.columnId);
          if (oldValue !== newColumnValue.value) {
            return true;
          }
        }
      }

      // Compare customColumns if they exist
      if (newItem.customColumns || oldItem.customColumns) {
        const newCustomColumns = newItem.customColumns || [];
        const oldCustomColumns = oldItem.customColumns || [];

        // Quick length check
        if (newCustomColumns.length !== oldCustomColumns.length) {
          return true;
        }

        // Create maps for custom columns comparison
        const oldCustomColumnsMap = new Map(
          oldCustomColumns.map(cc => [cc.id, cc]),
        );

        // Check if any custom column has changed
        for (const newCustomColumn of newCustomColumns) {
          const oldCustomColumn = oldCustomColumnsMap.get(newCustomColumn.id);
          if (!oldCustomColumn) {
            return true; // New custom column
          }

          // Check important properties of custom columns that might affect display
          const fieldsToCheck = [
            'title',
            'type',
            'isVisible',
            'width',
            'order',
            'sortingBy',
            'options',
          ];

          for (const field of fieldsToCheck) {
            // Deep comparison for options object
            if (field === 'options') {
              if (
                JSON.stringify(oldCustomColumn[field]) !==
                JSON.stringify(newCustomColumn[field])
              ) {
                return true;
              }
            } else if (oldCustomColumn[field] !== newCustomColumn[field]) {
              return true;
            }
          }
        }
      }

      // Also check other fields that might change (like status, deadline, etc.)
      const fieldsToCheck = [
        'name',
        'status',
        'deadline',
        'assignees',
        'updatedAt',
        'thumbnail',
      ];
      for (const field of fieldsToCheck) {
        // Handle array fields like assignees
        if (Array.isArray(newItem[field]) || Array.isArray(oldItem[field])) {
          if (
            JSON.stringify(newItem[field]) !== JSON.stringify(oldItem[field])
          ) {
            return true;
          }
        } else if (newItem[field] !== oldItem[field]) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Append new items to the existing rows without full re-render
   * @param {Array} newItems - Items to append
   */
  #appendNewItems(newItems) {
    if (!this.rowsWrapper || newItems.length === 0) {
      return;
    }

    // Update current items first
    CurrentItemsState.setCurrentItems([
      ...CurrentItemsState.currentItems,
      ...newItems,
    ]);

    // // If we're using virtual scrolling, update the virtual scroll setup
    // if (this.virtualScrolling.enabled && this.virtualScrolling.items) {
    //   this.virtualScrolling.items = [...CurrentItemsState.currentItems];
    //   this.virtualScrolling.containerHeight =
    //     CurrentItemsState.currentItems.length * this.virtualScrolling.itemHeight;

    //   // Find the virtual scroll container
    //   const children = this.rowsWrapper.getChildren();
    //   let scrollContainer = null;

    //   // Look for the scroll container (the one with a height style)
    //   for (let i = children.length - 1; i >= 0; i--) {
    //     const child = children[i];
    //     if (child.dom && child.dom.style.height) {
    //       scrollContainer = child;
    //       break;
    //     }
    //   }

    //   if (scrollContainer) {
    //     scrollContainer.setStyle('height', [
    //       `${this.virtualScrolling.containerHeight}px`,
    //     ]);
    //   }

    //   // // Update visible items if needed
    //   // this.updateVisibleItems();
    //   return;
    // }

    // Remove "no items found" message if it exists
    const existingChildren = this.rowsWrapper.getChildren();
    if (existingChildren.length === 1) {
      const firstChild = existingChildren[0];
      if (firstChild.dom && firstChild.dom.textContent === 'No items found') {
        this.rowsWrapper.clear();
      }
    }

    // Calculate the starting index for alternating colors
    const startIndex = CurrentItemsState.currentItems.length - newItems.length;

    // Append each new item
    newItems.forEach((item, idx) => {
      const isTask = !!item.reconstructions || !!item.commentsEnabled;
      const rowIndex = startIndex + idx;
      const row = this.#createRowForItem(item, isTask, rowIndex);
      this.rowsWrapper.add(row);
      this.rowElements.set(item.id, row);
      // this.visibleRows.add(item.id);
    });

    // Register new items with selection manager
    this.#registerItemsWithSelectionManager();
  }

  /**
   * Handle column events from external sources
   * @param {Object} data - The column event data
   */
  #handleColumnChange(data) {
    // Only respond to changes from the same folder
    if (data.currentFolderId === this.currentFolderId) {
      switch (data.type) {
        case COLUMN_EVENT_TYPES.VISIBILITY_CHANGED:
        case COLUMN_EVENT_TYPES.TITLE_CHANGED:
        case COLUMN_EVENT_TYPES.DELETED:
        case COLUMN_EVENT_TYPES.WIDTH_CHANGED:
        case COLUMN_EVENT_TYPES.SORTED:
        case COLUMN_EVENT_TYPES.RESET:
        case COLUMN_EVENT_TYPES.ORDERING:
          this.#refreshRows();
          break;

        case COLUMN_EVENT_TYPES.WIDTH_CHANGING:
          // Update cell widths in real-time during resize
          this.#updateCellWidthsRealTime(data.columnId, data.newWidth);
          break;

        case COLUMN_EVENT_TYPES.ADDED:
          // Handle column addition by adding new cells to existing rows
          this.#handleColumnAdded(data);
          break;

        default:
          break;
      }
    }
  }

  /**
   * Handle column addition by adding new cells to existing rows
   * @param {Object} data - The column addition event data
   */
  #handleColumnAdded(data) {
    if (!this.rowsWrapper || !data.column) return;

    // Since we need to add a new cell to each existing row and the logic for
    // maintaining row instances vs DOM elements is complex, the most reliable
    // approach is to refresh the rows to include the new column - if you have better ideas, feel free to suggest!
    this.#refreshRows();
  }

  /**
   * Refresh both the columns layout and rows with updated information
   */
  #refreshRowsAndColumns() {
    // Destroy existing components and clean up before rebuilding
    this.#destroyExistingComponents();

    // Clear the entire component and rebuild
    this.clear();

    // Recreate the columns layout
    this.add(this.#createColumnsLayout());

    // Recreate the rows
    this.add(this.#createRows());
  }

  /**
   * Refresh the rows with updated column information
   */
  #refreshRows() {
    if (!this.rowsWrapper) return;

    // Destroy existing row components before clearing
    if (this.rowElements && this.rowElements.size > 0) {
      this.rowElements.forEach((row, itemId) => {
        try {
          if (row && typeof row.destroy === 'function') {
            row.destroy();
          }
        } catch (error) {
          console.warn(`Error destroying row ${itemId}:`, error);
        }
      });
      this.rowElements.clear();
    }

    // // Clear visible rows tracking
    // if (this.visibleRows) {
    //   this.visibleRows.clear();
    // }

    // Clear existing rows
    this.rowsWrapper.clear();

    // Reset current items tracking
    CurrentItemsState.setCurrentItems([]);

    // Re-create rows with updated columns
    this.#createRowItems();

    // Re-register items with selection manager after refresh
    this.#registerItemsWithSelectionManager();
  }

  /**
   * Update cell widths in real-time during column resize
   * @param {string} columnId - The ID of the column being resized
   * @param {string} newWidth - The new width of the column
   */
  #updateCellWidthsRealTime(columnId, newWidth) {
    // Throttle updates for better performance
    if (this.realTimeUpdateThrottle) {
      clearTimeout(this.realTimeUpdateThrottle);
    }

    this.realTimeUpdateThrottle = setTimeout(() => {
      this.#performRealTimeWidthUpdate(columnId, newWidth);
    }, 0);
  }

  /**
   * Perform the actual real-time width update
   * @param {string} columnId - The ID of the column being resized
   * @param {string} newWidth - The new width of the column
   */
  #performRealTimeWidthUpdate(columnId, newWidth) {
    if (!this.rowsWrapper) return;

    // Get the current visible columns to find the column index
    const visibleColumns = getVisibleColumns(this.currentFolderId);
    const columnIndex = visibleColumns.findIndex(col => col.id === columnId);

    if (columnIndex === -1) return;

    // Find all row DOM elements and update the specific cell width
    const rowDOMElements = this.rowsWrapper.getChildren();
    rowDOMElements.forEach(rowDOM => {
      if (!rowDOM.children || rowDOM.children.length === 0) return;

      // Update the cell at the column index
      if (rowDOM.children.length > columnIndex) {
        const cellDOM = rowDOM.children[columnIndex];
        if (cellDOM) {
          // Work directly with DOM styles since we have DOM elements, not UIElements
          cellDOM.style.width = newWidth;
          cellDOM.style.minWidth = newWidth;
          cellDOM.style.maxWidth = newWidth;
        }
      }
    });
  }

  #initSelectionManager() {
    if (
      this.selectionManager &&
      typeof this.selectionManager.destroy === 'function'
    ) {
      this.selectionManager.destroy();
    }

    if (this.isAnonymousView) return;

    this.selectionManager = new SelectionManager({
      container: this.dom,
      itemSelector: '.project-list-view-row',
      selectedClass: 'selected-row',
      addedClass: 'bg-blue-100 border-blue-300',
      getItemId: el => {
        const itemId = el.dataset.projectListViewRowId;
        return itemId;
      },
      canSelectItem: el => el.dataset.projectListViewRowId,
      ignoreSelector:
        'button, input, select, a, [role="button"], .columns-container, .thumbnail-container, .grip-handle',
      onSelectionChange: (_, selectedItems) => {
        this.selectedItems = Array.from(selectedItems);
        this.#updateSelectionUI();
      },
      onDeleteKey: selectedElements => {
        if (selectedElements.length > 0) {
          this.#handleBulkDelete(selectedElements);
        }
      },
      onCopy: itemIds => {
        // this is a placeholder for copy functionality - if we remove it the copy functionality will not work
      },
      onPaste: async itemIds => {
        const itemsToCopy = CurrentItemsState.currentItems.filter(item =>
          itemIds.includes(item.id.toString()),
        );
        if (itemsToCopy.length > 0) {
          const isTask = itemsToCopy.some(
            item => !!item.reconstructions || !!item.commentsEnabled,
          );
          const duplicationFunction = isTask ? duplicateTask : duplicateProject;
          await duplicationFunction(itemsToCopy.map(item => item.id));
        } else {
          toast.error('No items copied for duplication');
        }
      },
      enableShiftSelect: true,
      enableCtrlSelect: true,
      enableDragSelect: true,
      enableRightClickSelect: true,
      clearSelectionOnOutsideClick: true,
    });

    // Register existing items with the selection manager
    this.#registerItemsWithSelectionManager();
  }

  /**
   * Register items with the selection manager
   */
  #registerItemsWithSelectionManager() {
    if (!this.selectionManager || !this.rowsWrapper) return;

    // Find all row elements and register them
    const rowElements = this.rowsWrapper.dom.querySelectorAll(
      '.project-list-view-row',
    );
    rowElements.forEach(rowElement => {
      const itemId = rowElement.dataset.projectListViewRowId;
      if (itemId) {
        // Find the item data
        const itemData = CurrentItemsState.currentItems.find(
          item => item.id === parseInt(itemId),
        );
        if (itemData) {
          this.selectionManager.registerItem(rowElement, itemData);
        }
      }
    });
  }

  async #handleShare(items) {
    if (items.length !== 1) return;

    const item = items[0];
    try {
      await shareProjectTask(item);
    } catch (error) {
      console.error('Error sharing item:', error);
    }
  }

  async #handleMove(items) {
    const selectedTasks = items.filter(item => item.isTask);
    if (selectedTasks.length === 0) return;

    try {
      const moveModal = new MoveModal({
        title: i18next.t('pages.projects.bottomActionBar.moveTasks', {
          count: selectedTasks.length,
        }),
        sourceFolderId: this.currentFolderId,
        isPersonal: this.selectedProjectType === 'personal',
        onMove: async targetFolderId => {
          try {
            const taskIds = selectedTasks.map(task => task.id);
            const sourceFolderId = this.currentFolderId;
            await moveTask(sourceFolderId, taskIds, targetFolderId);
            return false;
          } catch (error) {
            console.error('Error moving task:', error);
            throw error;
          }
        },
      });

      await moveModal.show();
    } catch (error) {
      console.error('Error moving tasks:', error);
    }
  }

  async #handleDuplicate(items) {
    try {
      const selectedTasks = items.filter(item => item.isTask);
      const selectedProjects = items.filter(item => !item.isTask);

      if (selectedTasks.length > 0) {
        const taskIds = selectedTasks.map(task => task.id);
        await duplicateTask(taskIds);
      }

      if (selectedProjects.length > 0) {
        const projectIds = selectedProjects.map(project => project.id);
        await duplicateProject(projectIds);
      }
    } catch (error) {
      console.error('Error duplicating items:', error);
    }
  }

  async #handleDelete() {
    const selectedElements = this.selectionManager.getSelectedItems();
    if (selectedElements.length === 0) {
      toast.error('No items selected for deletion');
      return;
    }
    await this.#handleBulkDelete(selectedElements);
  }

  async #handleOpenAiAnalyser(selectedItems, pdfFiles = []) {
    const selectedTasks = selectedItems.filter(item => item.isTask);
    const selectedProjects = selectedItems.filter(item => !item.isTask);
    const projectIds = selectedProjects.map(item => item.id);
    const taskIds = selectedTasks.map(item => item.id);

    // if we are on mobile, we will go to llm analyzer page
    if (IS_MOBILE()) {
      // Set state before navigation so it's available when LLM page mounts
      AiSelectedItems.setSelectedItems({
        projects: projectIds,
        tasks: taskIds,
      });

      if (pdfFiles.length > 0) {
        FoldersState.updateFolders(pdfFiles);
      }

      const urlParams = new URLSearchParams(window.location.search);

      if (this.currentFolderId) {
        urlParams.set('pId', this.currentFolderId);
      }

      // Persist selected items in URL params as a fallback for mobile restoration
      if (projectIds.length > 0) {
        urlParams.set('aiProjects', projectIds.join(','));
      }
      if (taskIds.length > 0) {
        urlParams.set('aiTasks', taskIds.join(','));
      }

      route('/llm?from=projects' + `&${urlParams.toString()}`);
      return;
    }

    const existingDrawer = DrawerState.getContent('ai-analyzer');
    let drawer;

    if (existingDrawer) {
      drawer = existingDrawer;
    } else {
      drawer = new DrawerAiAnalyser();
      MainDrawer.addContent(drawer, 'ai-analyzer');
    }

    // Open the drawer first, then set the context so it's applied to the live session
    MainDrawer.collapseSidebar('ai-analyzer', true);

    // Update the context selection on the live drawer instance.
    // This is done AFTER collapseSidebar to ensure any initialization triggered
    // by opening the drawer is complete before we apply the selection.
    drawer.updateContextSelection(projectIds, taskIds);

    if (pdfFiles.length > 0) {
      drawer.updateFiles(pdfFiles);
    }
  }

  /**
   * Initialize the bottom action bar
   */
  #initBottomActionBar() {
    if (this.isAnonymousView) return;

    this.bottomActionBar = new BottomActionBar({
      selectedItems: [],
      actions: [],
      onClearSelection: () => {
        if (this.selectionManager) {
          this.selectionManager.clearSelection();
        }
      },
      triggerRename: selectedItem => {
        // Find the row for the selected item and trigger rename
        const row = this.rowElements.get(selectedItem.id);
        if (row && typeof row.triggerNameEdit === 'function') {
          row.triggerNameEdit();
        }
      },
    });

    // Append to body so it appears on top of everything
    document.body.appendChild(this.bottomActionBar.dom);
  }

  /**
   * Update the selection UI (e.g., show action bar, update counts)
   */
  #updateSelectionUI() {
    const selectedCount = this.selectedItems.length;

    if (selectedCount > 0) {
      // Prepare selected items with proper metadata
      const selectedItemsWithMetadata = this.selectedItems
        .map(selectedItem => {
          const itemData = CurrentItemsState.currentItems.find(
            item => item.id === selectedItem.id,
          );

          if (itemData) {
            return {
              ...itemData,
              isTask: !!itemData.reconstructions || !!itemData.commentsEnabled,
            };
          }
          return null;
        })
        .filter(Boolean);

      // Update the bottom action bar
      if (this.bottomActionBar) {
        const availableActions = this.#getAvailableActions(
          selectedItemsWithMetadata,
        );
        this.bottomActionBar.updateSelection(
          selectedItemsWithMetadata,
          availableActions,
        );
      }
    } else {
      // Hide action bar if no items selected
      if (this.bottomActionBar) {
        this.bottomActionBar.updateSelection([]);
      }
    }
  }

  #getAvailableActions(items = []) {
    const actions = [];

    if (items.length === 0) return actions;

    // Determine what's selected
    const selectedTasks = items.filter(item => item.isTask);
    const selectedProjects = items.filter(item => !item.isTask);
    const hasOnlyTasks =
      selectedTasks.length > 0 && selectedProjects.length === 0;
    const singleItem = items.length === 1;
    // check if contains default folder which cannot be deleted
    const containsDefaultFolder = items.some(item => item.isDefault);

    if (!containsDefaultFolder && singleItem && this.canUpdate) {
      actions.push({
        id: 'rename',
        label: i18next.t('common.rename'),
        icon: EditIcon,
        handler: async () => {
          const row = this.rowElements.get(items[0].id);
          if (row && typeof row.triggerNameEdit === 'function') {
            row.triggerNameEdit();
          }
        },
      });
    }

    // Share action (only for single items)
    if (singleItem && this.canShare) {
      actions.push({
        id: 'share',
        label: i18next.t('common.share'),
        icon: ShareIcon,
        handler: async () => {
          await this.#handleShare(items);
          this.selectionManager.clearSelection();
        },
      });
    }

    // Move action (only for tasks)
    if (hasOnlyTasks && this.canUpdate && items.length <= BULK_ACTION_LIMIT) {
      actions.push({
        id: 'move',
        label: i18next.t('common.move'),
        icon: MoveFileIcon,
        handler: async () => {
          await this.#handleMove(items);
          this.selectionManager.clearSelection();
        },
      });
    }

    // Duplicate action
    if (this.canCreate && items.length <= BULK_ACTION_LIMIT) {
      actions.push({
        id: 'duplicate',
        label: i18next.t('common.duplicate'),
        icon: CopyIcon,
        handler: async () => {
          await this.#handleDuplicate(items);
          this.selectionManager.clearSelection();
        },
      });
    }

    // Delete action
    if (
      this.canDelete &&
      items.length <= BULK_ACTION_LIMIT &&
      !containsDefaultFolder
    ) {
      actions.push({
        id: 'delete',
        label: i18next.t('common.delete'),
        icon: TrashIcon,
        handler: async () => {
          await this.#handleDelete();
          this.selectionManager.clearSelection();
        },
      });
    }

    // only shows if selected items contain PDF reconstructions
    const hasPdfReconstructions = items
      .map(item => ({
        id: item.id,
        name: item.name,
        urls: item.reconstructions
          ?.filter(reconstruction => reconstruction.filePath?.endsWith('.pdf'))
          .map(reconstruction => ({
            path: reconstruction.filePath,
            originalName: reconstruction.fileOriginalName,
            fileSize: reconstruction.fileSize,
            selected: true,
            taskId: item.id,
          })),
      }))
      .filter(data => data.urls?.length > 0);

    // AI Context action - available for any selection (projects, tasks, or mixed)
    if (items.length <= BULK_ACTION_LIMIT) {
      actions.push({
        id: 'openAiAnalyzer',
        label: i18next.t('common.analyze'),
        icon: AiIcon,
        handler: async () => {
          await this.#handleOpenAiAnalyser(items, hasPdfReconstructions);
          this.selectionManager.clearSelection();
        },
      });
    }

    return actions;
  }

  /**
   * Handle bulk delete of selected items
   */
  async #handleBulkDelete(selectedElements) {
    const itemsToDelete = [];

    selectedElements.forEach(element => {
      const itemId = element.dataset.projectListViewRowId;
      const itemData = CurrentItemsState.currentItems.find(
        item => item.id.toString() === itemId,
      );
      if (itemData) {
        itemsToDelete.push({
          id: itemId,
          data: itemData,
          isTask: !!itemData.reconstructions || !!itemData.commentsEnabled,
        });
      }
    });

    if (itemsToDelete.length === 0) return;

    const isTask = itemsToDelete.some(item => item.isTask);

    const dialog = new YesNoPromptModal(
      i18next.t('pages.projects.delete', {
        type: isTask
          ? i18next.t('pages.projects.task')
          : i18next.t('pages.projects.project'),
      }),
      i18next.t('pages.projects.deleteConfirmation', {
        type: isTask
          ? i18next.t('pages.projects.task')
          : i18next.t('pages.projects.project'),
      }),
      {
        confirmText: i18next.t('common.delete'),
        cancelText: i18next.t('common.cancel'),
        confirmClass:
          '!bg-[var(--o-hive-error-100)] hover:!bg-[var(--o-hive-error-80)] text-white',
      },
    );

    const confirmed = await dialog.show();
    if (!confirmed) return false;

    try {
      if (isTask) {
        await deleteTasks(
          this.currentFolderId,
          itemsToDelete.map(item => parseInt(item.id)),
        );
      } else {
        await deleteProjects(itemsToDelete.map(item => parseInt(item.id)));
      }

      this.selectionManager.clearSelection();
    } catch (error) {
      console.error('Error deleting items:', error);
      toast.error('Failed to delete selected items');
    }
  }

  /**
   * Update access control permissions and refresh the view
   */
  updateAccessControl(accessControl) {
    this.canCreate = accessControl.hasPermission('create');
    this.canUpdate = accessControl.hasPermission('update');
    this.canDelete = accessControl.hasPermission('delete');
    this.canShare = accessControl.hasPermission('share');

    // Clear any existing selection since permissions may have changed
    if (this.selectionManager) {
      this.selectionManager.clearSelection();
    }

    // Force a refresh of the current view
    this.#refreshRows();
  }

  /**
   * Cleanup method to remove event subscriptions
   * Call this when the component is destroyed
   */
  destroy() {
    // Destroy the ProjectListColumns component specifically
    if (
      this.listViewColumns &&
      typeof this.listViewColumns.destroy === 'function'
    ) {
      try {
        this.listViewColumns.destroy();
        this.listViewColumns = null;
      } catch (error) {
        console.warn(
          'Error destroying ProjectListColumns during cleanup:',
          error,
        );
      }
    }

    // Clear throttled updates
    if (this.realTimeUpdateThrottle) {
      clearTimeout(this.realTimeUpdateThrottle);
      this.realTimeUpdateThrottle = null;
    }

    if (this.scrollUpdateThrottle) {
      clearTimeout(this.scrollUpdateThrottle);
      this.scrollUpdateThrottle = null;
    }

    // Remove scroll event listener
    // if (this.rowsContainer && this.rowsContainer.dom) {
    //   this.rowsContainer.dom.removeEventListener(
    //     'scroll',
    //     this.handleScroll.bind(this),
    //   );
    // }

    // Remove navigation event listener
    if (this.dom) {
      this.dom.removeEventListener(
        'navigateToFolder',
        this.navigateToFolder.bind(this),
      );
    }

    // Unsubscribe from signals
    if (this.columnUpdate) {
      columnUpdate.remove(this.#handleColumnChange);
    }

    if (this.subscribeFromProjectsState) {
      this.subscribeFromProjectsState();
      this.subscribeFromProjectsState = null;
    }

    // Call destroyExistingComponents for comprehensive cleanup
    this.#destroyExistingComponents();

    // Cleanup bottom action bar
    if (
      this.bottomActionBar &&
      typeof this.bottomActionBar.destroy === 'function'
    ) {
      try {
        this.bottomActionBar.destroy();
        this.bottomActionBar = null;
      } catch (error) {
        console.warn('Error destroying BottomActionBar:', error);
      }
    }

    // Clear cached row elements
    if (this.rowElements) {
      this.rowElements.clear();
    }

    // // Clear visible rows tracking
    // if (this.visibleRows) {
    //   this.visibleRows.clear();
    // }

    // Clear all component references
    CurrentItemsState.setCurrentItems([]);
    this.rowsContainer = null;
    this.rowsWrapper = null;
    // this.virtualScrolling = null;
  }

  async navigateToFolder(event) {
    const { folderId } = event.detail;
    try {
      // Destroy all existing components and event listeners before navigation
      this.#destroyExistingComponents();

      // Hide or remove bottomActionBar on folder navigation
      if (this.bottomActionBar) {
        this.bottomActionBar.hide();
        if (this.selectionManager) {
          this.selectionManager.clearSelection();
        }
      }

      // Update the current folder context
      this.currentFolderId = folderId;

      // Check if we need to fetch tasks (not already loaded)
      const needsFetch = !LoadedFolderIds.loadedFolderIds.includes(folderId);

      // Dispatch loading state event to parent container
      if (needsFetch) {
        this.#dispatchLoadingStateEvent(true);
      }

      // Fetch the folder data with its tasks if not already loaded
      const result = await ProjectsState.getProjectById(folderId);

      // Dispatch loading complete event
      if (needsFetch) {
        this.#dispatchLoadingStateEvent(false);
      }

      if (result.success) {
        // Update the state with the new folder and its tasks
        const currentState = ProjectsState.projects;
        const updatedFolders = [...currentState.folders];
        const updatedTasks = [...currentState.tasks];

        // Add or update the folder in the state
        const existingFolderIndex = updatedFolders.findIndex(
          f => f.id === folderId,
        );
        if (existingFolderIndex >= 0) {
          updatedFolders[existingFolderIndex] = result.project;
        } else if (result.project) {
          updatedFolders.push(result.project);
        }

        // Add tasks that belong to this folder (only if not from cache, as cache already has merged tasks)
        if (!result.fromCache && result.tasks && result.tasks.length > 0) {
          // Remove existing tasks for this folder to avoid duplicates
          const filteredTasks = updatedTasks.filter(
            task => task.parentId !== folderId,
          );
          updatedTasks.splice(
            0,
            updatedTasks.length,
            ...filteredTasks,
            ...result.tasks,
          );
        }

        // // Update the state (only if not from cache, as getProjectById already updated)
        if (!result.fromCache) {
          ProjectsState.setProjects({
            ...currentState,
            folders: updatedFolders,
            tasks: updatedTasks,
          });
        }

        // Refresh the view to show tasks for the selected folder
        this.#refreshRowsAndColumns();

        // Dispatch custom event to notify parent components of folder navigation
        this.#dispatchFolderNavigationEvent(folderId);
      }
    } catch (error) {
      console.error('Error navigating to folder:', error);
      this.#dispatchLoadingStateEvent(false);
      toast.error('Failed to load folder contents');
    }
  }

  /**
   * Dispatch loading state event to parent container
   * @param {boolean} isLoading - Whether loading is in progress
   */
  #dispatchLoadingStateEvent(isLoading) {
    const event = new CustomEvent('projectListLoading', {
      detail: {
        isLoading,
        folderId: this.currentFolderId,
        canCreate: this.canCreate,
        sharedProjectId: this.sharedProjectId,
      },
      bubbles: true,
    });
    this.dom.dispatchEvent(event);
  }

  navigateToAllProjects() {
    // Destroy all existing components and event listeners before navigation
    this.#destroyExistingComponents();

    // Hide or remove bottomActionBar on folder navigation
    if (this.bottomActionBar) {
      this.bottomActionBar.hide();
      if (this.selectionManager) {
        this.selectionManager.clearSelection();
      }
    }

    // Reset to show all projects
    this.currentFolderId = null;

    // Refresh the view
    this.#refreshRowsAndColumns();

    // Dispatch custom event to notify parent components of folder navigation
    this.#dispatchFolderNavigationEvent(null);
  }

  /**
   * Dispatch a custom event to notify parent components of folder navigation
   * @param {string|null} folderId - The folder ID navigated to (null for root projects)
   */
  #dispatchFolderNavigationEvent(folderId) {
    const event = new CustomEvent('folderNavigated', {
      detail: {
        folderId: folderId,
        folderName: folderId ? this.getFolderName(folderId) : null,
      },
      bubbles: true,
    });
    this.dom.dispatchEvent(event);
  }

  /**
   * Get folder name by ID for event details
   * @param {string} folderId - The folder ID
   * @returns {string|null} - The folder name or null if not found
   */
  getFolderName(folderId) {
    if (!folderId) return null;
    const folder = ProjectsState.projects.folders.find(f => f.id === folderId);
    return folder ? folder.name : null;
  }

  /**
   * Render items synchronously for small datasets
   * @param {Array} items - Items to render
   */
  #renderItemsSynchronously(items) {
    items.forEach((item, index) => {
      const isTask = !!item.reconstructions || !!item.commentsEnabled;
      const row = this.#createRowForItem(item, isTask, index);
      this.rowsWrapper.add(row);
      this.rowElements.set(item.id, row);
      // this.visibleRows.add(item.id);
    });

    // Register all items with the selection manager after rendering
    this.#registerItemsWithSelectionManager();
  }

  // /**
  //  * Setup virtual scrolling for large datasets
  //  * @param {Array} items - Items to render
  //  */
  // setupVirtualScrolling(items) {
  //   // Calculate container height
  //   this.virtualScrolling.containerHeight =
  //     items.length * this.virtualScrolling.itemHeight;

  //   // Create a placeholder container to maintain scroll height
  //   const scrollContainer = new UIDiv();
  //   scrollContainer.addClass('w-full');
  //   scrollContainer.setStyle('height', [
  //     `${this.virtualScrolling.containerHeight}px`,
  //   ]);

  //   // Create visible items container
  //   const visibleContainer = new UIDiv();
  //   visibleContainer.addClass('w-full absolute top-0 left-0');

  //   scrollContainer.add(visibleContainer);

  //   // Add the virtual scroll container first
  //   this.rowsWrapper.add(scrollContainer);

  //   // Store references for virtual scrolling
  //   this.virtualScrolling.items = items;
  //   this.virtualScrolling.visibleContainer = visibleContainer;

  //   // Initial render of visible items
  //   this.updateVisibleItems();
  // }

  /**
   * Handle scroll events for virtual scrolling
   */
  // handleScroll() {
  //   if (!this.virtualScrolling.enabled || !this.virtualScrolling.items) {
  //     return;
  //   }

  //   // Throttle scroll updates
  //   if (this.scrollUpdateThrottle) {
  //     clearTimeout(this.scrollUpdateThrottle);
  //   }

  //   this.scrollUpdateThrottle = setTimeout(() => {
  //     this.updateVisibleItems();
  //   }, 16); // ~60fps
  // }

  // /**
  //  * Update visible items based on scroll position
  //  */
  // updateVisibleItems() {
  //   if (!this.virtualScrolling.items || !this.rowsContainer) {
  //     return;
  //   }

  //   const scrollTop = this.rowsContainer.dom.scrollTop;
  //   const containerHeight = this.rowsContainer.dom.clientHeight;
  //   const itemHeight = this.virtualScrolling.itemHeight;
  //   const buffer = this.virtualScrolling.renderBuffer;

  //   // Calculate visible range
  //   const visibleStart = Math.max(
  //     0,
  //     Math.floor(scrollTop / itemHeight) - buffer,
  //   );
  //   const visibleCount = Math.ceil(containerHeight / itemHeight) + 2 * buffer;
  //   const visibleEnd = Math.min(
  //     this.virtualScrolling.items.length,
  //     visibleStart + visibleCount,
  //   );

  //   // Only update if range changed significantly
  //   if (
  //     visibleStart === this.virtualScrolling.visibleStart &&
  //     visibleEnd === this.virtualScrolling.visibleEnd
  //   ) {
  //     return;
  //   }

  //   this.virtualScrolling.visibleStart = visibleStart;
  //   this.virtualScrolling.visibleEnd = visibleEnd;

  //   // Queue async rendering of visible items
  //   this.renderVisibleItemsAsync(visibleStart, visibleEnd);
  // }

  // /**
  //  * Render visible items asynchronously to avoid blocking the UI
  //  * @param {number} start - Start index
  //  * @param {number} end - End index
  //  */
  // async renderVisibleItemsAsync(start, end) {
  //   // Clear current visible container
  //   if (this.virtualScrolling.visibleContainer) {
  //     this.virtualScrolling.visibleContainer.clear();
  //   }

  //   // Calculate offset for positioning
  //   const offsetTop = start * this.virtualScrolling.itemHeight;
  //   this.virtualScrolling.visibleContainer.setStyle('transform', [
  //     `translateY(${offsetTop}px)`,
  //   ]);

  //   // Render items in chunks to avoid blocking
  //   const chunkSize = 10;
  //   for (let i = start; i < end; i += chunkSize) {
  //     const chunkEnd = Math.min(i + chunkSize, end);
  //     await this.renderItemChunk(i, chunkEnd);

  //     // Yield control to the browser
  //     await new Promise(resolve => setTimeout(resolve, 0));
  //   }
  // }

  // /**
  //  * Render a chunk of items
  //  * @param {number} start - Start index in chunk
  //  * @param {number} end - End index in chunk
  //  */
  // async renderItemChunk(start, end) {
  //   const items = this.virtualScrolling.items;

  //   for (let i = start; i < end; i++) {
  //     const item = items[i];
  //     let row = this.rowElements.get(item.id);

  //     // Create row if not cached
  //     if (!row) {
  //       const isTask = !!item.reconstructions || !!item.commentsEnabled;
  //       row = this.#createRowForItem(item, isTask, i);
  //       this.rowElements.set(item.id, row);
  //     }

  //     this.virtualScrolling.visibleContainer.add(row);
  //     this.visibleRows.add(item.id);
  //   }
  // }

  // /**
  //  * Configure virtual scrolling settings
  //  * @param {Object} options - Configuration options
  //  */
  // configureVirtualScrolling(options = {}) {
  //   this.virtualScrolling = {
  //     ...this.virtualScrolling,
  //     ...options,
  //   };
  // }

  // /**
  //  * Disable virtual scrolling (useful for testing or small datasets)
  //  */
  // disableVirtualScrolling() {
  //   this.virtualScrolling.enabled = false;
  //   this.virtualScrolling.maxSyncItems = Infinity;
  // }

  // /**
  //  * Enable virtual scrolling with default or custom settings
  //  * @param {Object} options - Configuration options
  //  */
  // enableVirtualScrolling(options = {}) {
  //   this.virtualScrolling.enabled = true;
  //   this.configureVirtualScrolling(options);
  // }

  /**
   * Destroy all existing components and clean up memory before rebuilding
   * This method ensures proper cleanup to prevent memory leaks
   */
  #destroyExistingComponents() {
    // Specifically destroy the ProjectListColumns component first
    if (
      this.listViewColumns &&
      typeof this.listViewColumns.destroy === 'function'
    ) {
      try {
        this.listViewColumns.destroy();
        this.listViewColumns = null;
      } catch (error) {
        console.warn('Error destroying ProjectListColumns component:', error);
      }
    }

    // Destroy all cached row elements
    if (this.rowElements && this.rowElements.size > 0) {
      this.rowElements.forEach((row, itemId) => {
        try {
          if (row && typeof row.destroy === 'function') {
            row.destroy();
          }
        } catch (error) {
          console.warn(`Error destroying row ${itemId}:`, error);
        }
      });
      this.rowElements.clear();
    }

    // // Clear visible rows tracking
    // if (this.visibleRows) {
    //   this.visibleRows.clear();
    // }

    // Destroy any existing columns components and their nested components
    const children = this.getChildren();
    children.forEach(child => {
      try {
        // Then destroy the component itself
        if (child && typeof child.destroy === 'function') {
          child.destroy();
        }
      } catch (error) {
        console.warn('Error destroying child component:', error);
      }
    });

    // Reset current items tracking
    CurrentItemsState.setCurrentItems([]);

    // Clear virtual scrolling references
    if (this.virtualScrolling) {
      this.virtualScrolling.items = null;
      this.virtualScrolling.visibleContainer = null;
    }

    // Clear any cached DOM references
    this.rowsContainer = null;
    this.rowsWrapper = null;
  }

  /**
   * Set up drag and drop functionality for rows
   */
  #setupRowDragAndDrop() {
    if (!this.rowsWrapper || !this.canUpdate) {
      return;
    }

    // Add drop event listener to the rows wrapper
    this.rowsWrapper.onDragOver(e => {
      e.preventDefault();
    });

    this.rowsWrapper.onDrop(e => {
      e.preventDefault();
      this.#handleRowDrop(e);
    });
  }

  /**
   * A private helper to find a row element and its current DOM index.
   * @param {string} itemId - The ID of the item row to find.
   * @returns {{element: HTMLElement, index: number} | null} The element and its index, or null if not found.
   */
  #findRowInDom(itemId) {
    const rowElements = this.rowsWrapper
      .getChildren()
      .filter(el => el.hasAttribute('data-project-list-view-row-id'));

    const element = rowElements.find(
      el =>
        el.getAttribute('data-project-list-view-row-id') === itemId.toString(),
    );

    if (!element) return null;

    // get all row elements to now the new rowOrder of all items, this is necessary because if the new order moved a row to the top or bottom, the index will change for that row if the rowOrder was already defined for the others rows
    const elementIdIndexList = rowElements.map(el =>
      parseInt(el.getAttribute('data-project-list-view-row-id'), 10),
    );

    return { index: rowElements.indexOf(element), elementIdIndexList };
  }

  /**
   * Handle row drop event and update row order
   */
  #handleRowDrop(e) {
    try {
      const draggedData = e.dataTransfer.getData('application/x-row-data');
      if (!draggedData) return;

      const { itemId, isTask, currentFolderId } = JSON.parse(draggedData);
      const rowInfo = this.#findRowInDom(itemId);

      if (!rowInfo) {
        toast.error('Could not find the item to reorder.');
        return;
      }

      // Dispatch a single, clean event with all necessary data.
      projectListReOrder.dispatch({
        itemId,
        isTask,
        currentFolderId,
        newPosition: rowInfo.index, // Pass the new position
        elementIdIndexList: rowInfo.elementIdIndexList, // Pass the list of element IDs in order
      });
    } catch (error) {
      console.error('Error handling row drop:', error);
      toast.error('An error occurred while dropping the item.');
    }
  }

  /**
   * Handle row reorder
   */
  async #handleRowReorder(data) {
    const { itemId, isTask, currentFolderId } = data;
    let newPosition = data.newPosition; // This can be undefined if not provided
    let elementIdIndexList = data.elementIdIndexList; // This can be undefined if not provided

    try {
      // If newPosition isn't provided (e.g., from a touch event that finished),
      if (newPosition === undefined) {
        const rowInfo = this.#findRowInDom(itemId);
        if (!rowInfo) {
          toast.error('Could not find the item to reorder.');
          return;
        }
        newPosition = rowInfo.index;
        elementIdIndexList = rowInfo.elementIdIndexList; // Use the list of element IDs in order
      }

      const { folders, tasks } = ProjectsState.projects;
      const rowsToUpdate = isTask
        ? tasks.filter(task => task.parentId === currentFolderId)
        : folders;

      const draggedRow = rowsToUpdate.find(item => item.id === itemId);

      if (!draggedRow) {
        toast.error('Could not find the item to reorder.');
        return;
      }

      const originalPosition = draggedRow.rowOrder;

      await updateRowOrders({
        items: rowsToUpdate,
        itemId: itemId,
        newPosition: newPosition,
        idIndexList: elementIdIndexList, // Pass the list of element IDs in order
        originalPosition: originalPosition,
        isTask: isTask,
      });
    } catch (error) {
      toast.error('Failed to save the new order.');
    }
  }
}
